import { Autocomplete, Card, FormControl, Input, InputLabel } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useFormikContext } from "formik";
import React, { useEffect } from "react";
import InputMaskWrapper from "@/components/atoms/InputMaskWrapper";

import FormField from "@/components/atoms/FormField";
import { BrazilPhoneMaskInput } from "@/components/atoms/MaskInput";
import MDBox from "@/components/atoms/MDBox";
import MDInput from "@/components/atoms/MDInput";
import MDTypography from "@/components/atoms/MDTypography";
import { ContactFormData } from "@/components/screens/ContactFormPage/types";
import { GENDER_OPTIONS } from "@/constants/gender.constants";
import { formatDate } from "@/helpers/date-helpers";
import {
  applyDocumentMask,
  determineDocumentType,
  formatWithMask,
} from "@/helpers/document-helpers";
import { handlePhoneChanges } from "@/helpers/phone-helpers";
import { ContactInfoMessages } from "@/messages/contacts/contact-Info.messages";
import { PersonType } from "@/types/document.types";

type Props = {
  messages: ContactInfoMessages;
  isEditing: boolean | undefined;
};

function formatContactDate(value: Date | string | null | undefined): string {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return formatDate(date);
}

function resolveConsentDateDisplay(
  consentDate: Date | string | null | undefined,
  consentStatus: string | null | undefined,
  pendingLabel: string
): string {
  const hasConsentStatus = Boolean(consentStatus?.toString().trim());
  const hasConsentDate =
    consentDate !== null && consentDate !== undefined && String(consentDate).trim() !== "";

  if (!hasConsentDate && !hasConsentStatus) return pendingLabel;
  if (!consentDate) return pendingLabel;

  const formatted = formatContactDate(consentDate);
  return formatted || pendingLabel;
}

const READ_ONLY_FIELD_PROPS = {
  variant: "standard" as const,
  fullWidth: true,
  disabled: true,
  InputLabelProps: { shrink: true },
};

const ContactInfo = ({ messages, isEditing }: Props) => {
  const formik = useFormikContext<ContactFormData>();
  const { values, handleChange, errors, setFieldValue } = formik;

  useEffect(() => {
    const mask = applyDocumentMask(values.document);

    if (mask && values.document) {
      const formattedValue = formatWithMask(values.document.replace(/\D/g, ""), mask);
      const documentType = determineDocumentType(values.document.replace(/\D/g, ""));

      if (formattedValue !== values.document) {
        setFieldValue("documentType", documentType);
      }

      setFieldValue("documentType", documentType);
    }
  }, [values.document, setFieldValue]);

  const genderOptions = GENDER_OPTIONS(messages);

  const registrationDate = formatContactDate(values.rowDate);
  const lastModifiedDate = formatContactDate(values.lastModifiedDate);
  const consentDateDisplay = resolveConsentDateDisplay(
    values.consentDate,
    values.consentStatus,
    messages.consentPendingLabel
  );

  return (
    <Card>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight="bold">
            {messages.contactInformationTitle}
          </MDTypography>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 0 }}>
              <FormField
                variant="standard"
                name="firstName"
                label={messages.firstNameLabel}
                value={values.firstName}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.firstName}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 0 }}>
              <FormField
                variant="standard"
                name="lastName"
                label={messages.lastNameLabel}
                value={values.lastName}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.lastName}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 0 }}>
              <FormField
                variant="standard"
                name="nickName"
                label={messages.nickNameLabel}
                value={values.nickName}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.nickName}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 0 }}>
              <FormField
                variant="standard"
                name="email"
                label={messages.emailLabel}
                value={values.email}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.email}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 0 }}>
              <DatePicker
                label={messages.birthDateLabel}
                value={dayjs(typeof values.birthDate === "string" ? values.birthDate : null)}
                onChange={(value) => setFieldValue("birthDate", value ? value.format() : "")}
                slotProps={{
                  textField: {
                    helperText: errors.birthDate,
                    error: Boolean(errors.birthDate),
                    fullWidth: true,
                    variant: "standard",
                    InputLabelProps: { shrink: true },
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 0 }}>
              <Autocomplete
                fullWidth
                disabled={values.persontype === PersonType.LegalEntity}
                options={genderOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                value={genderOptions.find((option) => option.value === values.gender) || null}
                onChange={(_, newValue) => {
                  setFieldValue("gender", newValue ? newValue.value : "");
                }}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    label={messages.genderLabel}
                    variant="standard"
                    error={!!errors.gender}
                    helperText={errors.gender}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 0 }}>
              <InputMaskWrapper
                mask={applyDocumentMask(values.document)}
                value={values.document as string}
                onChange={handleChange}
                variant="standard"
                name="document"
                label={messages.documentLabel}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.document}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 0 }}>
              <FormControl variant="standard" fullWidth>
                <InputLabel htmlFor="formatted-text-mask-input">
                  {messages.mobilePhoneLabel}
                </InputLabel>
                <Input
                  value={values.fullMobilePhone}
                  onChange={(e) =>
                    handlePhoneChanges("fullMobilePhone", e.target.value, setFieldValue)
                  }
                  name="fullMobilePhone"
                  inputComponent={BrazilPhoneMaskInput}
                />
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 0 }}>
              <FormControl variant="standard" fullWidth>
                <InputLabel htmlFor="formatted-text-mask-input">{messages.phone1Label}</InputLabel>
                <Input
                  value={values.fullPhone1}
                  onChange={(e) => handlePhoneChanges("fullPhone1", e.target.value, setFieldValue)}
                  name="fullPhone1"
                  inputComponent={BrazilPhoneMaskInput}
                />
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 0 }}>
              <FormControl variant="standard" fullWidth>
                <InputLabel htmlFor="formatted-text-mask-input">{messages.phone2Label}</InputLabel>
                <Input
                  value={values.fullPhone2}
                  onChange={(e) => handlePhoneChanges("fullPhone2", e.target.value, setFieldValue)}
                  name="fullPhone2"
                  inputComponent={BrazilPhoneMaskInput}
                />
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 0 }}>
              <MDInput
                {...READ_ONLY_FIELD_PROPS}
                label={messages.registrationDateLabel}
                value={isEditing ? registrationDate : ""}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 0 }}>
              <MDInput
                {...READ_ONLY_FIELD_PROPS}
                label={messages.lastModifiedDateLabel}
                value={isEditing ? lastModifiedDate : ""}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 0 }}>
              <MDInput
                {...READ_ONLY_FIELD_PROPS}
                label={messages.consentDateLabel}
                value={isEditing ? consentDateDisplay : messages.consentPendingLabel}
              />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
};

export default ContactInfo;
